/**
 * funChallengesTournamentStore
 *
 * Lightweight zustand store for Puzzle Royale TOURNAMENT socket notices that
 * aren't part of the in-race royale store. Holds:
 *  - lastCreatedId : durable id echoed by fun_tournament_created (host feedback)
 *  - stateById     : latest fun_tournament_state per tournament id
 *                    (registered / live / cancelled + reason), so an open lobby
 *                    / host panel can react (e.g. show "called off — not enough
 *                    players", navigate into the battle on live).
 *
 * Reads (list/detail) come from the REST hook; this store is only the live
 * socket-pushed deltas layered on top.
 */

import {create} from 'zustand';

export interface TournamentStateNotice {
  tournamentId: string;
  state:
    | 'registered'
    | 'withdrawn'
    | 'live'
    | 'completed'
    | 'cancelled'
    | string;
  reason?: string;
  shareCode?: string;
  roomId?: string;
  playerCount?: number;
  at: number;
}

interface TournamentStore {
  lastCreatedId: string | null;
  lastCreatedAt: number;
  stateById: Record<string, TournamentStateNotice>;
  setCreated: (id: string) => void;
  setState: (notice: TournamentStateNotice) => void;
  clearCreated: () => void;
}

const useFunChallengesTournamentStore = create<TournamentStore>(set => ({
  lastCreatedId: null,
  lastCreatedAt: 0,
  stateById: {},
  setCreated: id =>
    set({lastCreatedId: String(id), lastCreatedAt: Date.now()}),
  setState: notice =>
    set(s => ({
      stateById: {...s.stateById, [String(notice.tournamentId)]: notice},
    })),
  clearCreated: () => set({lastCreatedId: null}),
}));

export default useFunChallengesTournamentStore;
